import type { User } from '@supabase/supabase-js';

/**
 * Processes stored user role after OAuth login and updates user metadata.
 * Kept separate from `auth.ts` so `useAuth` does not statically depend on the Supabase client bundle.
 */
export async function processOAuthUserRole(user: User) {
  try {
    const pendingRole = sessionStorage.getItem('pendingUserRole');

    if (!pendingRole) {
      return;
    }

    if (user.user_metadata?.user_type) {
      sessionStorage.removeItem('pendingUserRole');
      return;
    }

    let internalUserType: string;
    switch (pendingRole) {
      case 'tenant':
        internalUserType = 'tenant';
        break;
      case 'propertyManager':
        internalUserType = 'agent';
        break;
      case 'landlord':
        internalUserType = 'landlord';
        break;
      default:
        internalUserType = 'tenant';
    }

    const { supabase } = await import('./supabase');

    const { error } = await supabase.auth.updateUser({
      data: {
        ...user.user_metadata,
        user_type: internalUserType,
      },
    });

    if (error) {
      console.error('Error updating user role:', error);
      return;
    }

    try {
      const { error: profileError } = await supabase.from('user_profiles').insert({
        user_id: user.id,
        full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
        phone: user.user_metadata?.phone || null,
        avatar_url: user.user_metadata?.avatar_url || null,
        user_type: internalUserType,
        preferred_contact: 'email',
      });

      if (profileError && profileError.code !== '23505') {
        console.warn('Failed to create user profile:', profileError);
      }
    } catch (profileError) {
      console.warn('Error creating user profile:', profileError);
    }

    sessionStorage.removeItem('pendingUserRole');

    console.log('Successfully assigned role to OAuth user:', internalUserType);
  } catch (err) {
    console.error('Error processing OAuth user role:', err);
    sessionStorage.removeItem('pendingUserRole');
  }
}
